import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, MailOpen } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { formatDate } from "@/lib/format";

export default function MessagesPanel({ onCount }) {
  const { tr } = useLang();
  const a = tr("admin");
  const [messages, setMessages] = useState(null);

  const load = () => {
    api
      .get("/admin/messages")
      .then((r) => {
        setMessages(r.data || []);
        onCount((r.data || []).filter((m) => !m.read).length);
      })
      .catch(() => setMessages([]));
  };

  useEffect(load, []);

  const markRead = async (m) => {
    await api.put(`/admin/messages/${m.id}/read`);
    toast.success(a.msgRead);
    load();
  };

  return (
    <div data-testid="admin-messages-panel">
      <h2 className="mb-8 font-serif text-2xl text-ivory">{a.tabs.messages}</h2>
      {messages === null ? (
        <p className="py-16 text-center font-mono text-xs uppercase tracking-[0.25em] text-ivory/40">…</p>
      ) : messages.length === 0 ? (
        <p className="border border-gold/15 py-16 text-center text-sm text-ivory/50" data-testid="admin-messages-empty">
          {a.messagesEmpty}
        </p>
      ) : (
        <div className="space-y-4" data-testid="admin-messages-list">
          {messages.map((m) => (
            <div
              key={m.id}
              data-testid={`admin-message-${m.id}`}
              className={`border p-6 ${m.read ? "border-white/10 bg-white/[0.02]" : "border-gold/40 bg-gold/[0.06]"}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-serif text-lg text-ivory">{m.name}</span>
                  {!m.read && (
                    <span data-testid={`message-unread-${m.id}`} className="bg-gold px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-navy">
                      {a.new}
                    </span>
                  )}
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-ivory/40">{formatDate(m.created_at, "it")}</span>
              </div>
              <p className="mt-2 font-mono text-xs text-gold-bright/90">
                {a.from}: {m.email}
                {m.company ? ` · ${m.company}` : ""}
              </p>
              <p className="mt-4 border-t border-white/10 pt-4 text-sm leading-relaxed text-ivory/70">{m.message}</p>
              {!m.read && (
                <button
                  onClick={() => markRead(m)}
                  data-testid={`mark-read-${m.id}`}
                  className="mt-5 inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-ivory/60 transition-colors hover:border-gold/50 hover:text-gold-bright"
                >
                  <MailOpen className="h-3.5 w-3.5" /> {a.markRead}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
